import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid } from "@mui/material";
import {
  formStateHandler,
  getISODateTime,
  checkErrorStateExists,
} from "../../../../utils/utils";
import TextInput from "../../../../framework/TextInput";
import SelectInput from "../../../../framework/SelectInput";
import GlobalContext from "../../../../framework/GlobalContext";
import { gstTypes, stateCodes } from "../../../../utils/constant";
import _ from "lodash";
import { displayToast } from "../../../../framework/toast/toastUtils";
import DatePicker from "../../../../framework/date/DatePicker";
import dayjs from "dayjs";
const LicenseTab = ({
  validateForm = () => {},
  removeErrorStateOnChange = () => {},
}) => {
  const [distributorType, setDistributorType] = useState(null);
  const [stateCode, setStateCode] = useState(null);
  const [localFormState, setLocalFormState] = useState(null);
  const [licValidDate, setLicValidDate] = useState(null);
  const addDistributorGstinRef = useRef();
  const addDistributorDLNoRef = useRef();
  const addDistributorLicValidDateRef = useRef();
  const addDistributorCstVatRef = useRef();
  const addDistributorTypeRef = useRef();
  const addDistributorStateCodeRef = useRef();
  const { formState, errorState } = useSelector(
    (state) => state.commonReducer.common.addDistributor
  );
  const dispatch = useDispatch();
  const { setCurrentComponent } = useContext(GlobalContext);
  const dispatchFormState = (newState) => {
    dispatch({
      type: "set_add_distributor_form_state",
      payload: newState,
    });
  };
  const debounceDispatchFormState = _.debounce(dispatchFormState, 1);
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options) => {
      let newFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options
      );
      debounceDispatchFormState(newFormState);
    },
    [debounceDispatchFormState, formState]
  );
  const initializeForm = useCallback(
    (formState) => {
      if (formState) {
        addDistributorGstinRef.current.value = formState.distributorGstin || "";
        addDistributorLicValidDateRef.current.value =
          formState.licValidDate || null;
        if (formState.gstType !== null) {
          setDistributorType(
            gstTypes.filter((value) => value.id === formState?.gstType)[0]
          );
        }
        if (formState.gstState !== null) {
          setStateCode(
            (prev) => stateCodes.filter((s) => s.id === formState?.gstState)[0]
          );
        }
        if (formState.licValidDate) {
          const parsedDate =
            dayjs(formState.licValidDate).format("DD/MM/YYYY") || "";
          setLicValidDate(parsedDate);
          setLocalFormState((prev) => ({
            ...prev,
            licValidDate: parsedDate,
          }));
        }
      }
    },
    [addDistributorGstinRef]
  );

  const validateGstin = useCallback((gstin) => {
    const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
    if (!gstinRegex.test(gstin)) {
      displayToast({
        type: "error",
        title: "Please enter a valid GSTIN.",
      });
      return;
    }
    displayToast({
      type: "success",
      title: "GSTIN is valid.",
    });
    return true;
  }, []);

  useEffect(() => {
    if (formState && !localFormState) {
      setLocalFormState(formState);
      initializeForm(formState);
    }
  }, [formState, initializeForm, localFormState]);

  return (
    <Grid
      sx={{
        margin: "5px 0px 5px -10px",
        width: "100%",
      }}
      container
      columnSpacing={2}
      rowSpacing={2.5}
      columns={12}
    >
      <Grid item xs={3}>
        <TextInput
          id="add_distributor_dl_number"
          name="dlno"
          getInputRef={addDistributorDLNoRef}
          label="DL Number"
          onFocus={() => setCurrentComponent("add_distributor_dl_number")}
          onChange={(e) => {
            setLocalFormState((prev) => {
              return { ...prev, dlno: e.target.value };
            });
            handleFormChange("dlno", e.target.value);
          }}
          decimalScale={0}
          allowNegative={false}
          value={localFormState?.dlno}
          autoFocus
          inputProps={{
            "data-markers": JSON.stringify({
              up: "",
              down: "add_distributor_msme_reg_no",
              left: "",
              right: "add_distributor_license_validity_date",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <DatePicker
          id="add_distributor_license_validity_date"
          name="licValidDate"
          label="License Valid Till"
          inputRef={addDistributorLicValidDateRef}
          onFocus={() =>
            setCurrentComponent("add_distributor_license_validity_date")
          }
          onChange={(value) => {
            setLicValidDate(value);
            setLocalFormState((prev) => {
              return { ...prev, licValidDate: value };
            });
            handleFormChange(
              "licValidDate",
              getISODateTime(value.format("DD/MM/YYYY"))
            );
          }}
          value={dayjs(licValidDate, "DD/MM/YYYY")}
          inputProps={{
            "data-markers": JSON.stringify({
              left: "add_distributor_dl_number",
              right: "add_distributor_cstvat_number",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextInput
          id="add_distributor_cstvat_number"
          name="cstVat"
          ref={addDistributorCstVatRef}
          label="CST VAT Number"
          onFocus={() => setCurrentComponent("add_distributor_cstvat_number")}
          onChange={(e) => {
            setLocalFormState((prev) => {
              return { ...prev, cstVat: e.target.value };
            });
            handleFormChange("cstVat", e.target.value);
          }}
          decimalScale={0}
          allowNegative={false}
          value={localFormState?.cstVat}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_dl_number",
              down: "add_distributor_state_code",
              left: "add_distributor_license_validity_date",
              right: "add_distributor_gstin",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextInput
          id="add_distributor_gstin"
          name="distributorGstin"
          ref={addDistributorGstinRef}
          label="GSTIN"
          value={localFormState?.distributorGstin || ""}
          onFocus={() => setCurrentComponent("add_distributor_gstin")}
          onChange={(e) => {
            const upperValue = e.target.value.toUpperCase();
            setLocalFormState((prev) => ({
              ...prev,
              distributorGstin: upperValue,
            }));
            handleFormChange("distributorGstin", upperValue);
            removeErrorStateOnChange("add_distributor_distributorGstin");
          }}
          required
          onBlur={() => {
            const gstin = localFormState?.distributorGstin;
            validateGstin(gstin);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_cstvat_number",
              down: "add_distributor_type",
              left: "add_distributor_cstvat_number",
              right: "add_distributor_msme_reg_no",
            }),
          }}
          error={checkErrorStateExists(
            "add_distributor_distributorGstin",
            errorState
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <TextInput
          id="add_distributor_msme_reg_no"
          name="msmeRegNo"
          ref={addDistributorGstinRef}
          label="MSME Registration Number"
          value={localFormState?.msmeRegNo || ""}
          onFocus={() => setCurrentComponent("add_distributor_msme_reg_no")}
          onChange={(e) => {
            const upperValue = e.target.value.toUpperCase();
            setLocalFormState((prev) => ({
              ...prev,
              msmeRegNo: upperValue,
            }));
            handleFormChange("msmeRegNo", upperValue);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_dl_number",
              down: "add_distributor_type",
              left: "add_distributor_gstin",
              right: "add_distributor_type",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectInput
          id="add_distributor_type"
          name="gstType"
          ref={addDistributorTypeRef}
          label="GST Type"
          onFocus={() => setCurrentComponent("add_distributor_type")}
          onChange={(event, value) => {
            setDistributorType(value);
            handleFormChange("gstType", null, true, value);
            removeErrorStateOnChange("add_distributor_gstState");
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={gstTypes}
          value={distributorType || null}
          getOptionLabel={(option) => option.label}
          markers={JSON.stringify({
            up: "add_distributor_license_validity_date",
            down: "add_distributor_state_code",
            left: "add_distributor_msme_reg_no",
            right: "add_distributor_state_code",
          })}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectInput
          id="add_distributor_state_code"
          name="gstState"
          ref={addDistributorStateCodeRef}
          label="State Code"
          onFocus={() => setCurrentComponent("add_distributor_state_code")}
          onChange={(event, value) => {
            setStateCode(value);
            handleFormChange("gstState", null, true, value);
          }}
          onBlur={() => validateForm("gstState", formState)}
          required
          isOptionEqualToValue={(option, value) => option.label === value.label}
          options={stateCodes}
          getOptionLabel={(option) => option.label}
          value={stateCode || null}
          markers={JSON.stringify({
            up: "add_distributor_cstvat_number",
            down: "add_distributor_dl_number",
            left: "add_distributor_type",
            right: "add_distributor_dl_number",
          })}
          error={checkErrorStateExists("add_distributor_gstState", errorState)}
        />
      </Grid>
    </Grid>
  );
};

export default LicenseTab;
